import { useState, useEffect, useCallback, useRef } from "react";

/**
 * useDebounce
 * Returns a debounced version of the value that only updates after
 * the specified delay has elapsed since the last change.
 *
 * @param {any}    value  - The value to debounce (e.g. a search string)
 * @param {number} delay  - Debounce delay in milliseconds (default: 500)
 * @returns {any}          The debounced value
 *
 * @example
 * const debouncedSearch = useDebounce(searchTerm, 500);
 * useEffect(() => { fetchResults(debouncedSearch); }, [debouncedSearch]);
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * useDebouncedCallback
 * Returns a stable, debounced version of the provided callback function.
 * Useful when you want to debounce a function call rather than a state value
 * (e.g. firing an API search directly from an input handler).
 *
 * @param {Function} callback - The function to debounce
 * @param {number}   delay    - Debounce delay in milliseconds (default: 500)
 * @returns {Function}          The debounced callback (stable reference)
 *
 * @example
 * const handleSearch = useDebouncedCallback((query) => {
 *   fetchResults(query);
 * }, 500);
 */
export function useDebouncedCallback(callback, delay = 500) {
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);

  // Keep the ref in sync with the latest callback without restarting the timer
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return debouncedFn;
}

export default useDebounce;
